import { mergeRefs } from '@ipfabric/frontend-shared-assets/utils/react/mergeRefs';
import { useAutoFocus } from '@ipfabric/ui-components/hooks/useAutoFocus';
import type { StringKey } from '@ipfabric/utils';
import classNames from 'classnames';
import FormErrorMessage from 'components/FormErrorMessage';
import type { FormikProps } from 'formik';
import type { ReactNode, RefObject } from 'react';
import type { ChangeEvent, FocusEvent, JSX } from 'react';
import { fieldHasError } from 'utils/formik/fieldHasError';
import {
  type FormikAutoTrim,
  getTrimFunction,
} from 'utils/formik/formikAutoTrim';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';

export interface FormikTextFieldLegacyProps<TFormikValues> {
  addonAfter?: ReactNode;
  addonBefore?: ReactNode;
  autoComplete?: string;
  autoFocus?: boolean;
  autoTrim?: FormikAutoTrim;
  className?: string;
  errorClassName?: string;
  formik: FormikProps<TFormikValues>;
  id?: string;
  inputRef?: RefObject<HTMLInputElement>;
  name: StringKey<TFormikValues> | NestedFormikName;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  wrapperClassName?: string;
  type?: string;
}

export const FormikTextFieldLegacy = <TFormikValues,>(
  props: FormikTextFieldLegacyProps<TFormikValues>,
): JSX.Element => {
  const field = props.formik.getFieldProps(props.name);
  const { error, touched } = props.formik.getFieldMeta(props.name as string);
  const hasError = fieldHasError<TFormikValues>(props.formik, props.name);

  const inputRef = useAutoFocus(props.autoFocus);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const trimFunction = getTrimFunction(props.autoTrim);

    trimFunction(field.onChange)(e);
    if (props.onChange) {
      trimFunction(props.onChange)(e);
    }
  };

  return (
    <>
      <div
        className={classNames(
          props.wrapperClassName,
          (props.addonAfter || props.addonBefore) && 'input-group',
        )}
      >
        {props.addonBefore}
        <input
          {...field}
          autoComplete={
            props.autoComplete ??
            (props.type === 'password' ? 'off' : props.autoComplete)
          }
          className={classNames(
            props.className,
            hasError && 'formik-field__error',
          )}
          id={props.id}
          onBlur={props.onBlur}
          onFocus={props.onFocus}
          onChange={handleChange}
          placeholder={props.placeholder}
          ref={mergeRefs(props.inputRef, inputRef)}
          type={props.type ?? 'text'}
        />
        {props.addonAfter}
      </div>
      {error && touched && (
        <FormErrorMessage className={props.errorClassName}>
          {error}
        </FormErrorMessage>
      )}
    </>
  );
};
